/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
import { EuiIcon } from '../icon';
import { EuiI18n } from '../i18n';
var statusToClassNameMap = {
  complete: 'euiTourStepIndicator--complete',
  incomplete: 'euiTourStepIndicator--incomplete',
  active: 'euiTourStepIndicator--active'
};
export var STATUS = keysOf(statusToClassNameMap);
export var EuiTourStepIndicator = ({
  className,
  number,
  status,
  ...rest
}) => {
  const classes = classNames('euiTourStepIndicator', status ? statusToClassNameMap[status] : undefined, className);
  let indicatorIcon;

  if (status === 'active') {
    indicatorIcon = <EuiI18n token="euiTourStepIndicator.isActive" default="active">
        {isActive => <EuiIcon type="dot" className="euiStepNumber__icon" aria-label={isActive} color="success" aria-current="step" />}
      </EuiI18n>;
  } else if (status === 'complete') {
    indicatorIcon = <EuiI18n token="euiTourStepIndicator.isComplete" default="complete">
        {isComplete => <EuiIcon type="dot" className="euiStepNumber__icon" aria-label={isComplete} color="subdued" />}
      </EuiI18n>;
  } else if (status === 'incomplete') {
    indicatorIcon = <EuiI18n token="euiTourStepIndicator.isIncomplete" default="incomplete">
        {isIncomplete => <EuiIcon type="dot" className="euiStepNumber__icon" aria-label={isIncomplete} color="subdued" />}
      </EuiI18n>;
  }

  return <EuiI18n token="euiTourStepIndicator.ariaLabel" default="Step {number} {status}" values={{
    status,
    number
  }}>
      {ariaLabel => <li className={classes} aria-label={ariaLabel} {...rest}>
          {indicatorIcon}
        </li>}
    </EuiI18n>;
};
EuiTourStepIndicator.propTypes = {
  number: PropTypes.number.isRequired,
  status: PropTypes.oneOf(["complete", "incomplete", "active"]).isRequired,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiTourStepIndicator.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/tour/tour_step_indicator.tsx',
    description: '',
    displayName: 'EuiTourStepIndicator',
    methods: [],
    props: {
      number: {
        defaultValue: null,
        description: '',
        name: 'number',
        parent: {
          fileName: 'app/src/components/tour/tour_step_indicator.tsx',
          name: 'EuiTourStepIndicatorProps'
        },
        declarations: [{
          fileName: 'app/src/components/tour/tour_step_indicator.tsx',
          name: 'EuiTourStepIndicatorProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      status: {
        defaultValue: null,
        description: '',
        name: 'status',
        parent: {
          fileName: 'app/src/components/tour/tour_step_indicator.tsx',
          name: 'EuiTourStepIndicatorProps'
        },
        declarations: [{
          fileName: 'app/src/components/tour/tour_step_indicator.tsx',
          name: 'EuiTourStepIndicatorProps'
        }],
        required: true,
        type: {
          name: 'enum',
          raw: '"incomplete" | "complete" | "active"',
          value: [{
            value: '"incomplete"'
          }, {
            value: '"complete"'
          }, {
            value: '"active"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiTourStepIndicatorProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}